import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import mutations from "./mutations.js";
import actions from "./actions.js";
import { AUTH_REFRESH_MODE } from "./auth-refresh-mode.js";
import { createInitialState } from "./initial-state.mjs";
import { isAuthTransitioning } from "@/utils/auth-storage-sync.mjs";
import { getAuthPersistedState, isCookieRefreshEnabled, purgePersistedAuthTokens } from "@/utils/auth-tokens";
import { applyProjectMenuRoutes } from "@/utils/menu-permission";

Vue.use(Vuex);

purgePersistedAuthTokens(window.localStorage, AUTH_REFRESH_MODE);

function createSessionPersistedState(options) {
  return store => createPersistedState({
    ...options,
    filter: () => !isAuthTransitioning(store.state),
  })(store);
}

export default new Vuex.Store({
  plugins: [
    createSessionPersistedState({
      key: "auth",
      reducer: getAuthPersistedState,
      storage: window.localStorage,
    }),
    createSessionPersistedState({
      key: "recolumn",
      paths: [
        "step_level",
        "project_list",
        "current_project",
        "current_project_title",
        "current_project_pk",
        "current_project_sort",
        "entity_label_list",
        "file_list",
        "db_list",
        "statistics_list",
        "ocr_list",
        "rpa_result_list",
        "current_rpa",
        "rpa_view",
        "terminologyList",
        "current_terminology_id",
        "filter_word_list",
        "append_file_list",
        "disableDownload",
        "asyncTaskStatusList",
        "currentAsyncTask",
        "currentAsyncTaskLogList",
        "currentUploadDataCnt",
        "totalUploadDataCnt",
        "mouListDataToEdit",
        "lastInnerDb",
        "editingCellClickStep",
        "editTableDragAreaIdxObj",
        "currentTemplate",
        "file_import_data_columnSelectList1",
        "current_insight",
        "current_insight_folder",
        "async_error_block_id",
      ],
      storage: window.localStorage,
    }),
  ],

  state: createInitialState(AUTH_REFRESH_MODE),

  getters: {
    isAccessTokenExpired: (state) => () => {
      if (!state.access_token && !state.id_token) { // 로그인 이전 상황 - 토큰 갱신 프로세스가 돌지 않아야하기 때문에 토큰 만료되지 않음 처리
        const hasPersistedUser = Object.keys(state.user_info || {}).length > 0
        console.log('isAccessTokenExpired 1', isCookieRefreshEnabled(state.auth_refresh_mode) && hasPersistedUser, '토큰 없음 - cookie 모드에서 사용자 정보가 있으면 refresh 필요')
        return isCookieRefreshEnabled(state.auth_refresh_mode) && hasPersistedUser
      }

      try {
        if (state.user_info.user_type) {
          let userType = state.user_info.user_type
          let exp = null
          if (userType === 'google') {
            if (!state.id_token) { // user_info는 있는데 id_token이 없는 상황 - 로직 변경 전에 로그인하였거나 jwtDecode를 실행할 id_token이 없기 때문에 토큰 만료 처리하여 토큰 갱신 실행
              console.log('isAccessTokenExpired 2', true, 'user_info는 있는데 id_token이 없는 상황 - 로직 변경 전에 로그인하였거나 jwtDecode를 실행할 id_token이 없기 때문에 토큰 만료 처리하여 토큰 갱신 실행')
              return true
            }
            exp = jwtDecode(state.id_token).exp
          }
          else if (userType === 'django' || userType === 'django-prosecution') {
            if (!state.access_token) { // user_info는 있는데 id_token이 없는 상황 - 에러 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리
              console.log('isAccessTokenExpired 3', false, 'user_info는 있는데 id_token이 없는 상황 - 에러 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리')
              return false
            }
            exp = jwtDecode(state.access_token).exp
          }
          console.log(`${userType} exp`, exp) // 초(s) 단위
          const date_now = Date.now() / 1000 // 밀리초(ms) -> 초(s) 변환
          console.log(`${userType} date_now`, date_now)
          const bufferTime = 5 * 60 // 5분(300초) 여유시간
          console.log(`${userType} bufferTime`, bufferTime)
          console.log(`${userType} date_now + bufferTime`, date_now + bufferTime)
          console.log(`${userType} (date_now + bufferTime) - exp`, date_now + bufferTime - exp) // 양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리
          console.log('isAccessTokenExpired 4', date_now + bufferTime > exp, '양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리')
          return date_now + bufferTime > exp

          // if (userType === 'google') {
          //   if (!state.id_token) { // user_info는 있는데 id_token이 없는 상황 - 로직 변경 전에 로그인하였거나 jwtDecode를 실행할 id_token이 없기 때문에 토큰 만료 처리하여 토큰 갱신 실행
          //     console.log('isAccessTokenExpired 2', true, 'user_info는 있는데 id_token이 없는 상황 - 로직 변경 전에 로그인하였거나 jwtDecode를 실행할 id_token이 없기 때문에 토큰 만료 처리하여 토큰 갱신 실행')
          //     return true
          //   }
          //   const { exp } = jwtDecode(state.id_token)
          //   console.log('google exp', exp) // 초(s) 단위
          //   const date_now = Date.now() / 1000 // 밀리초(ms) -> 초(s) 변환
          //   console.log('google date_now', date_now)
          //   const bufferTime = 2 * 60 // 2분(120초) 여유시간
          //   console.log('google bufferTime', bufferTime)
          //   console.log('google date_now + bufferTime', date_now + bufferTime)
          //   console.log('google (date_now + bufferTime) - exp', date_now + bufferTime - exp) // 양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리
          //   console.log('isAccessTokenExpired 3', date_now + bufferTime > exp, '양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리')
          //   return date_now + bufferTime > exp
          // }
          // else if (userType === 'django') {
          //   if (!state.access_token) { // user_info는 있는데 id_token이 없는 상황 - 에러 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리
          //     console.log('isAccessTokenExpired 4', false, 'user_info는 있는데 id_token이 없는 상황 - 에러 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리')
          //     return false
          //   }
          //   const { exp } = jwtDecode(state.access_token)
          //   console.log('django exp', exp) // 초(s) 단위
          //   const date_now = Date.now() / 1000 // 밀리초(ms) -> 초(s) 변환
          //   console.log('django date_now', date_now)
          //   const bufferTime = 2 * 60 // 2분(120초) 여유시간
          //   console.log('django bufferTime', bufferTime)
          //   console.log('django date_now + bufferTime', date_now + bufferTime)
          //   console.log('django (date_now + bufferTime) - exp', date_now + bufferTime - exp) // 양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리
          //   console.log('isAccessTokenExpired 5', date_now + bufferTime > exp, '양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리')
          //   return date_now + bufferTime > exp
          // }
        }
        else { // 예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리
          console.log('isAccessTokenExpired 5', false, '예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리')
          return false
        }
      } catch { // 예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리
        console.log('isAccessTokenExpired 6', false, '예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리')
        return false
      }
    },
    isRefreshTokenExpired: (state) => () => {
      if (isCookieRefreshEnabled(state.auth_refresh_mode)) return false
      if (!state.refresh_token) { // 로그인 이전 상황 - 토큰 갱신 프로세스가 돌지 않아야하기 때문에 토큰 만료되지 않음 처리
        console.log('isRefreshTokenExpired 1', false, '로그인 이전 상황 - 토큰 갱신 프로세스가 돌지 않아야하기 때문에 토큰 만료되지 않음 처리')
        return false
      }

      try {
        if (state.user_info.user_type) {
          let userType = state.user_info.user_type
          let exp = null
          if (userType === 'google') {
            console.log('isRefreshTokenExpired 2', false, '구글 로그인은 토큰 만료되지 않음 처리')
            return false
          }
          else if (userType === 'django') {
            exp = jwtDecode(state.refresh_token).exp
            console.log(`${userType} exp`, exp) // 초(s) 단위
            const date_now = Date.now() / 1000 // 밀리초(ms) -> 초(s) 변환
            console.log(`${userType} date_now`, date_now)
            const bufferTime = 5 * 60 // 5분(300초) 여유시간
            console.log(`${userType} bufferTime`, bufferTime)
            console.log(`${userType} date_now + bufferTime`, date_now + bufferTime)
            console.log(`${userType} (date_now + bufferTime) - exp`, date_now + bufferTime - exp) // 양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리
            console.log('isRefreshTokenExpired 4', date_now + bufferTime > exp, '양수(true): 만료예상시간(exp)이 넘어서 토큰 만료처리 후 토큰 갱신, 음수(false): 만료예상시간(exp)이 넘지 않아어서 토큰 만료되지 않음 처리')
            return date_now + bufferTime > exp
          }
        }
        else { // 예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리
          console.log('isRefreshTokenExpired 5', false, '예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리')
          return false
        }
      } catch { // 예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리
        console.log('isRefreshTokenExpired 6', false, '예외 상황이기 때문에 토큰 갱신 실행할 수 없으므로 토큰 만료되지 않음 처리')
        return false
      }
    },
  
    baseUrl() {
      // return process.env.VUE_APP_API_URL
      if (process.env.NODE_ENV === "production") {
        return 'https://api.recolumn.com'
        // return 'https://tortue.cogcom.kr:8000'
      } else {
        return 'https://api-dev.recolumn.com'
        // return 'https://api.recolumn.com:8001'
      }
    },
    isLoggedIn(state) {
      if (
        state.access_token &&
        state.access_token !== null &&
        state.access_token !== ""
      ) {
        return true;
      }
      if (isCookieRefreshEnabled(state.auth_refresh_mode) && Object.keys(state.user_info || {}).length > 0) {
        return true;
      }
      return false;
    },
  
    permissionLevel(state) {
      let result = 0
      if (
        state.current_project && state.current_project.level !== null && state.current_project.level !== undefined && 
        state.user_info && state.user_info.license_permission !== null && state.user_info.license_permission !== undefined
      ) {
        if (state.current_project.level === 4) { // 프로젝트 오너인 경우
          result = 4
        } else {
          result = Math.min(state.current_project.level, state.user_info.license_permission)
        }
      }
      return result
    },

    drawerItems: (state) => { // new
      return applyProjectMenuRoutes([
        { title: '타임라인', title_en: 'Timeline',
          sub: '작업 내역 및 소통', sub_en: 'Activity History and Collaboration',
          value: 'dashboard',
          to: `/dashboard/${state.current_project_pk}`,
          routeName: 'DashBoard',
        },
        { title: '데이터', title_en: 'Data',
          sub: '연산부터 행/열 재배치까지', sub_en: 'Calculations & Data Restructuring',
          value: 'data-management',
          to: `/data-management/${state.current_project_pk}`,
          routeName: 'DataManagement',
        },
        { title: '프로세스', title_en: 'Process',
          sub: '파일, 사람, 작업 간 관계 분석', sub_en: 'Analyze relationships between files, people, and tasks',
          value: 'process',
          to: `/process/${state.current_project_pk}`,
          routeName: 'Process',
        },
        { title: '워크플로우', title_en: 'Workflow',
          sub: '모든 반복 작업을 원클릭으로', sub_en: 'Automate all repetitive tasks',
          value: 'rpa2', // RPA2
          to: `/rpa/${state.current_project_pk}`,
          routeName: 'RpaList',
        },
        // { title: '견적서', title_en: 'Form',
        //   sub: '옵션 선택이 있는 폼 자동 작성', sub_en: 'Auto-fill forms with options',
        //   value: 'estimate',
        //   to: `/estimate/${state.current_project_pk}`,
        //   routeName: 'Estimate',
        // },
        // { title: '문서 폼', title_en: 'Form',
        //   sub: '옵션 선택이 있는 폼 자동 작성', sub_en: 'Auto-fill forms with options',
        //   value: 'estimate',
        //   to: `/form-list/${state.current_project_pk}`,
        //   routeName: 'FormList',
        // },
        { title: '테이블', title_en: 'Table',
          sub: '-', sub_en: '-',
          value: 'table',
          to: `/table/${state.current_project_pk}`,
          routeName: 'Table',
        },
        { title: '시각화', title_en: 'Charts',
          sub: '자유로운 차트 대시보드', sub_en: 'Flexible Chart Dashboards',
          value: 'insight',
          to: `/insight-list/${state.current_project_pk}`,
          routeName: 'InsightList',
        },
        { title: '업무', title_en: 'Work',
          sub: '업무 flow, 할일, 실행 이력 관리', sub_en: 'Work flow, items, runs, and outputs',
          value: 'work',
          to: `/work-management/${state.current_project_pk}`,
          routeName: 'WorkManagement',
        },
        { title: 'AI Agent', title_en: 'AI Agent',
          sub: '백그라운드에서 실행될 엔진', sub_en: 'Background Processing Engine',
          value: 'ai-engine',
          to: `/ai-agent/${state.current_project_pk}`,
          routeName: 'AiAgent',
        },
        { title: '이메일 검증', title_en: 'Format and Validity Check',
          sub: '형식 및 유효성 검증', sub_en: 'Format and Validation',
          value: 'email-verify',
          to: `/email-verify/${state.current_project_pk}`,
          routeName: 'EmailVerify',
        },
        { title: '표 검출', title_en: 'Table Detection',
          sub: '비정형 시트에서 표 영역 검출', sub_en: 'Detect table areas from sheets.',
          value: 'table-detection',
          to: `/table-detection/${state.current_project_pk}`,
          routeName: 'TableDetection',
        },
        { title: '검출 결과', title_en: 'Detection Results',
          sub: '비정형 시트에서 표 영역 검출', sub_en: 'Table Detection Results',
          value: 'detection-result',
          to: `/detection-result/${state.current_project_pk}`,
          routeName: 'DetectionResult',
        },
        { title: 'Inner DB', title_en: 'Inner DB',
          sub: 'Beta', sub_en: 'Beta',
          value: 'inner-db',
          to: `/inner-db/${state.current_project_pk}`,
          routeName: 'InnerDb',
        },
        { title: 'OCR', title_en: 'OCR',
          sub: 'Beta', sub_en: 'Beta',
          value: 'ocr', 
          to: `/ocr/${state.current_project_pk}`,
          routeName: 'Ocr',
        },
        { title: 'PDF 분류', title_en: 'PDF Classification',
          sub: 'Beta', sub_en: 'Beta',
          value: 'pdf-classification', 
          to: `/pdf-classification/${state.current_project_pk}`,
          routeName: 'PdfClassification',
        },
        { title: '정보 추출', title_en: 'Data Extraction',
          sub: 'Beta', sub_en: 'Beta',
          value: 'extraction', parent_value: 'group-text-analyze',
          to: `/extraction/${state.current_project_pk}/1`,
          routeName: 'Extraction',
        },
        { title: '키워드', title_en: 'Keywords',
          sub: 'Beta', sub_en: 'Beta',
          value: 'keyword', parent_value: 'group-text-analyze',
          to: `/keyword/${state.current_project_pk}/1`,
          routeName: 'Keyword',
        },
        { title: '요약', title_en: 'Summary',
          sub: 'Beta', sub_en: 'Beta',
          value: 'summary', parent_value: 'group-text-analyze',
          to: `/summary/${state.current_project_pk}/1`,
          routeName: 'Summary',
        },
        { title: '텍스트 분석', title_en: 'Text Analysis',
          sub: 'Beta', sub_en: 'Beta',
          value: 'scatter', parent_value: 'group-text-analyze',
          to: '/scatter',
          routeName: 'Scatter',
        },
        { title: 'RPA', title_en: 'RPA',
          sub: 'Beta', sub_en: 'Beta',
          value: 'rpa',
          to: `/rpa/${state.current_project_pk}`,
          routeName: 'LegacyRpaList',
          disabled: true,
        },
        { title: 'MOU 리스트', title_en: 'MOU List',
          sub: 'Beta', sub_en: 'Beta',
          sub2: 'MOU 서류와 사업자등록증 파일을 업로드하면 계약기간, 법인명, 사업자등록번호, 법인등록번호를 추출해 DB화합니다.',
          value: 'mou-list', parent_value: 'group-custom',
          to: `/mou-list/${state.current_project_pk}`,
          routeName: 'MouList',
        },
        { title: '법인판매현황', title_en: 'Corporate Sales Report',
          sub: 'Beta', sub_en: 'Beta',
          sub2: '엔진이 각 법인별 판매현황을 자동으로 갱신합니다. <br> 사용자가 지정한 조건에 따라 특정 지사를 제외시킬 수 있습니다.',
          value: 'corporate-sales-status', parent_value: 'group-custom',
          to: `/corporate-sales-status/${state.current_project_pk}`,
          routeName: 'CorporateSalesStatus',
        },
        { title: 'IROS 검색', title_en: 'IROS Search',
          sub: 'Beta', sub_en: 'Beta',
          sub2: '고유번호 10건에 대해서 IROS(인터넷등기소) 검색을 실시합니다. <br> 데이터에서 적용할 Row 범위를 고를 수 있습니다.',
          value: 'iros-search', parent_value: 'group-custom',
          to: `/iros-search/${state.current_project_pk}`,
          routeName: 'IrosSearch',
        },
      ], state.current_project_pk)
    },

    yearList() {
      let result = []
      let startIdx = 1990
      let endIdx = Number(dayjs().year())
      for (let i = startIdx; i <= endIdx; i++) {
        let obj = { text: `${i}년`, value: i }
        result.push(obj)
      }
      result.sort((a, b) => b.value - a.value) // 내림차순
      console.log('yearList', result)
      return result
    },
    quarterList() {
      let result = []
      let startIdx = 1
      let endIdx = 4
      for (let i = startIdx; i <= endIdx; i++) {
        let obj = { text: `${i}분기`, value: i }
        result.push(obj)
      }
      // result.sort((a, b) => b.value - a.value) // 내림차순
      console.log('quarterList', result)
      return result
    },
    monthList() {
      let result = []
      let startIdx = 1
      let endIdx = 12
      for (let i = startIdx; i <= endIdx; i++) {
        let obj = { text: `${i}월`, value: i }
        result.push(obj)
      }
      // result.sort((a, b) => b.value - a.value) // 내림차순
      console.log('monthList', result)
      return result
    },
    dayList() {
      let result = []
      let startIdx = 1
      let endIdx = 31
      for (let i = startIdx; i <= endIdx; i++) {
        let obj = { text: `${i}일`, value: i }
        result.push(obj)
      }
      // result.sort((a, b) => b.value - a.value) // 내림차순
      console.log('dayList', result)
      return result
    },
  
    toastTimer() {
      return 3500
    },
    toastBgColor() {
      return '#FFFFFF'
    },
    toastWidth() {
      return 440
    },
  
    // 이미지 파일 아이콘
    imgFileIconColor() {
      return '#0000FF' // 파란색
    },
    // pdf 파일 아이콘
    pdfFileIconColor() {
      return '#EA4335' // 빨간색
    },
    // json 파일 아이콘
    jsonFileIconColor() {
      return '#37474F' // 어두운 남색
    },
    // rpa 파일 아이콘
    rpaFileIconColor() {
      return '#FF5721' // 진한 주황색
    },
    // common 파일 아이콘
    commonFileIconColor() {
      return '#2E7D32' // 초록색
    },
    // 
    dotSuccessColor() {
      return '#4CAF50' // 초록색
    },
    // 
    dotErrorColor() {
      return '#FF5252' // 빨간색
    },
    // 즐겨찾기 default(false) 색상
    bookmarkDefaultColor() {
      return '#8A8DA8' // 회색
    },
    // 즐겨찾기 check(true) 색상
    bookmarkCheckColor() {
      return '#EAC54F' // 회색
    },
  },

  mutations,
  actions,
});
